// Reference data on the device: copied from CRM whenever there's signal, read
// from the device always — so measuring works in a basement.
import { getReference, isConnectionFailure } from './api';
import { deviceMatchesSite } from './device-crm';
import { getReferenceList, saveReferenceList } from './device-db';
import type { AccessTag, SurfaceType } from '@higgins/m2q-pricing';
import { CONDITION_CATEGORY } from './condition';

/** Fetch the latest surface types and site-requirement tags into device storage. No signal = keep the copies already there. */
export async function refreshReference(): Promise<void> {
  // Not while the device holds another CRM's jobs (lib/device-crm.ts).
  if (!navigator.onLine || !(await deviceMatchesSite())) return;
  try {
    const { surfaceTypes, accessTags } = await getReference();
    await saveReferenceList('surfaceTypes', surfaceTypes);
    // An API from before the tag list sends none; keep what the device has.
    if (accessTags) await saveReferenceList('accessTags', accessTags);
  } catch (err) {
    if (!isConnectionFailure(err)) console.error('Could not refresh reference data', err);
  }
}

/** The device's surface types (empty if never fetched), keyed by id. */
export async function loadSurfaceTypes(): Promise<{ list: SurfaceType[]; byId: Map<string, SurfaceType> }> {
  const list = await getReferenceList('surfaceTypes');
  return { list, byId: new Map(list.map((t) => [t.id, t])) };
}

/** The device's site-requirement tags (empty if never fetched), in list order. */
export const loadAccessTags = (): Promise<AccessTag[]> => getReferenceList('accessTags');

/** The ticks offered under "Access, safety and preliminaries": every category but Condition, which has its own tab. */
export const loadSiteRequirementTags = async (): Promise<AccessTag[]> => (await loadAccessTags()).filter((t) => t.category !== CONDITION_CATEGORY);

/** The condition ticks (Customer facing, Entrance …), set up in CRM. */
export const loadConditionTicks = async (): Promise<AccessTag[]> => (await loadAccessTags()).filter((t) => t.category === CONDITION_CATEGORY);
